import React, { useState } from "react";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import SelectedServicesPanel from "src/components/shared-components/selected-services-panel";
import { useTranslate } from "src/locales";
import { useSelector } from "react-redux";
import { RootState } from "src/app/store";
import { Backdrop, CircularProgress, Box } from "@mui/material";

// Hooks
import { useRequestServiceSelection } from "./hooks/useRequestServiceSelection";
import { useRequestCategoryManagement } from "./hooks/useRequestCategoryManagement";
import { useRequestModalFlow } from "./hooks/useRequestModalFlow";
import { useRequestServiceValidation } from "./hooks/useRequestServiceValidation";

// Components
import RequestSidebarArea from "./partials/RequestSidebarArea";
import RequestServiceGridArea from "./partials/RequestServiceGridArea";
import EditOperationModal from "./partials/EditOperationModal";

// Sub-Modals
import {
  ProcessDirectionModal,
  AskDoctorModal,
  PregnantControlModal,
  HIVModal,
  PacemakerModal,
  ConsultationModal,
  LabRequestModal,
  PathologyModal,
  ServiceAddKonstrastModal,
  ServiceRequestKonstrastModal,
  InvestigationRequestNoteModal,
  ServiceRequestVkiModal,
  ServiceAddVkiModal,
  ServiceAddEgfrModal,
} from "src/components/Add-Service-Modals";

interface RequestServiceModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description: string;
  rowID: string;
  upn?: string;
}

const RequestServiceModal: React.FC<RequestServiceModalProps> = ({
  open,
  onClose,
  title, // Patient Name
  description,
  rowID,
  upn, // File No
}) => {
  const { t } = useTranslate();
  const selectedOfficeId = useSelector(
    (state: RootState) => state.auth.selectedOfficeId
  );

  // Local state for UI inputs that are shared or needed by multiple hooks
  const [requestNote, setRequestNote] = useState("");
  const [urgencyLevel, setUrgencyLevel] = useState<number>(0);
  const [procedureDate, setProcedureDate] = useState<Date>(new Date());
  const [isPregnant, setIsPregnant] = useState<boolean>(false);


  const categoryHook = useRequestCategoryManagement(open, rowID);

  const [selectedServices, setSelectedServices] = useState<(number | string)[]>([]);
  const [selectedHistoryServices, setSelectedHistoryServices] = useState<
    (number | string)[]
  >([]);

  const modalFlowHook = useRequestModalFlow({
    rowID,
    selectedOfficeId: selectedOfficeId || 0,
    setSelectedServices,
    setSelectedHistoryServices,
  });

  const flowActions = {
    setWillOpenCardFlowValid: modalFlowHook.setWillOpenCardFlowValid,
    setPendingWillOpenCards: modalFlowHook.setPendingWillOpenCards,
    setActiveWillOpenCardIndex: modalFlowHook.setActiveWillOpenCardIndex,
    setPendingConfirmSelection: modalFlowHook.setPendingConfirmSelection,
    setActiveWillOpenCardServiceName: modalFlowHook.setActiveWillOpenCardServiceName,
    openModalForCard: modalFlowHook.openModalForCard,
    removeAddInfoForService: modalFlowHook.removeAddInfoForService,
  };

  // 4. Selection Hook (Lifted state passed in)
  const selectionHook = useRequestServiceSelection(
    rowID,
    categoryHook.selectedSubCategory,
    categoryHook.currentView,
    categoryHook.mainSearchTerm,
    selectedOfficeId || 0,
    flowActions,
    selectedServices, // NEW PROP
    setSelectedServices, // NEW PROP
    selectedHistoryServices,
    setSelectedHistoryServices,
    modalFlowHook.pendingConfirmSelection,
    modalFlowHook.pendingWillOpenCards
  );

  // 5. Validation Hook
  // Wrapper to handle both state cleanup and actual closing
  const handleFullyClose = () => {
    selectionHook.handleModalClose();
    onClose();
  };

  // 4. Validation Hook
  const validationHook = useRequestServiceValidation({
    rowID,
    selectedServicesData: selectionHook.selectedServicesData,
    selectedHistoryServicesData: selectionHook.selectedHistoryServicesData,
    serviceAddInfo: modalFlowHook.serviceAddInfo,
    currentView: categoryHook.currentView,
    handleModalClose: handleFullyClose, // Uses the wrapper
    requestNote,
    procedureDate,
    isPregnant,
    urgencyLevel,
  });

  // Reset form fields when modal opens
  React.useEffect(() => {
    if (open) {
      setProcedureDate(new Date()); // Reset to current datetime when modal opens
    }
  }, [open]);

  // Effect to trigger fetching when view or category changes
  React.useEffect(() => {
    if (!open) return;

    if (categoryHook.currentView === "frequent") {
      let orIdToUse: number | undefined = undefined;

      // If a specific category is selected in Frequent view, use its objectid/orId
      if (categoryHook.selectedSubCategory) {
        const selectedCat = categoryHook.categories.find(c => c.id === categoryHook.selectedSubCategory);
        if (selectedCat && selectedCat.objectid) {
          orIdToUse = Number(selectedCat.objectid);
        }
      }

      selectionHook.fetchFavorites(orIdToUse);
    } else if (categoryHook.currentView === "history") {
      selectionHook.fetchPatientHistory(Number(rowID));
    } else if (categoryHook.currentView === "subcategory" && categoryHook.selectedSubCategory) {
      const selectedCat = categoryHook.categories.find(c => c.id === categoryHook.selectedSubCategory);
      if (selectedCat) {
        // Ensure ids is an array of numbers
        let ids: number[] = [];

        if (selectedCat.subtypeIds && Array.isArray(selectedCat.subtypeIds)) {
          ids = selectedCat.subtypeIds.map((id: any) => Number(id)).filter((n: number) => !isNaN(n));
        }

        if (ids.length > 0) {
          // orId should be the category's objectid (or privid?), NOT selectedOfficeId. 
          // Based on context, objectid is likely OR_ID.
          const orIdToUse = selectedCat.objectid ? Number(selectedCat.objectid) : 0;
          selectionHook.fetchCategoryServices(ids, orIdToUse);
        }
      }
    }
  }, [
    categoryHook.currentView,
    categoryHook.selectedSubCategory,
    categoryHook.categories,
    open,
    rowID,
    selectionHook.fetchFavorites,
    selectionHook.fetchPatientHistory,
    selectionHook.fetchCategoryServices
  ]);

  return (
    <OpenComponentDialog
      open={open}
      onClose={selectionHook.handleModalClose}
      maxWidth="xl"
      fullWidth
      hideHeader={true}
      showPatientData={false}
    >
      <Box
        sx={{
          height: "auto",
          minHeight: "90vh",
          display: "flex",
          transition: "height 0.3s ease-in-out",
          position: "relative",
          pb: "60px",
          overflow: "auto",
        }}
      >
        <RequestSidebarArea
          title={title}
          upn={upn || ""}
          protocolId={categoryHook.protocolId}
          categories={categoryHook.sidebarSearchText ? categoryHook.filteredCategories : categoryHook.categories}
          selectedMainCategory={categoryHook.selectedMainCategory}
          selectedSubCategory={categoryHook.selectedSubCategory}
          expandedCategories={categoryHook.expandedCategories}
          sidebarSearchText={categoryHook.sidebarSearchText}
          handleMainCategorySelect={categoryHook.handleMainCategorySelect}
          handleSubCategorySelect={categoryHook.handleSubCategorySelect}
          handleCategoryToggle={categoryHook.handleCategoryToggle}
          handleSidebarSearch={categoryHook.handleSidebarSearch}
        />

        <RequestServiceGridArea
          currentView={categoryHook.currentView}
          selectedSubCategory={categoryHook.selectedSubCategory}
          mainSearchTerm={categoryHook.mainSearchTerm}
          setMainSearchTerm={categoryHook.setMainSearchTerm}
          requestNote={requestNote}
          setRequestNote={setRequestNote}
          urgencyLevel={urgencyLevel}
          setUrgencyLevel={setUrgencyLevel}
          procedureDate={procedureDate}
          setProcedureDate={setProcedureDate}
          isPregnant={isPregnant}
          setIsPregnant={setIsPregnant}




          gridData={selectionHook.categoryServices}
          flattenedHistoryData={
            (selectionHook as any).flattenedHistoryData || []
          }
          historyDataLength={selectionHook.historyData.length}
          loadingServices={selectionHook.loadingServices}
          isServiceProcessing={selectionHook.isServiceProcessing}
          loadingHistoryData={selectionHook.loadingHistoryData}
          historyParentPage={selectionHook.historyParentPage}
          setHistoryParentPage={selectionHook.setHistoryParentPage}
          historyParentPageSize={selectionHook.historyParentPageSize}
          setHistoryParentPageSize={selectionHook.setHistoryParentPageSize}

          selectedServices={selectedServices}
          selectedHistoryServices={selectionHook.selectedHistoryServices}
          expandedHistoryRows={selectionHook.expandedHistoryRows}
          favoriteServiceIds={selectionHook.favoriteServiceIds}
          // Actions
          onClose={handleFullyClose}

          handleServiceToggle={selectionHook.handleServiceToggle}
          handleHistoryServiceToggle={(id, data) => (selectionHook as any).handleHistoryServiceToggle(id, data)}
          handleHistoryParentToggle={(id) => (selectionHook as any).handleHistoryParentToggle(id)}
          handleParentToggle={(id) => (selectionHook as any).handleParentToggle(id)} // Same?
          handleAddToFavorites={selectionHook.handleAddToFrequent}
          handleRemoveFromFavorites={selectionHook.handleRemoveFromFrequent}
        />
      </Box>

      <SelectedServicesPanel
        data={(selectionHook as any).combinedSelectedServicesData || []}
        selectedItems={(selectionHook as any).combinedSelectedItems || []}
        config={{
          title: t("patient-request-service.selected-services.title"),
          showTestDurations: true,
          showPatientDebt: true,
          showAddToPatient: true,
          showVisibilityButton: true,
          showFullScreenButton: true,
          showEditButton: true,
        }}
        // Simplified toggle handler passing for SelectedServicesPanel
        onItemToggle={(id: any) => {
          // Check if this id is in history selections or regular selections
          if (selectionHook.selectedHistoryServices.includes(id)) {
            (selectionHook as any).handleHistoryServiceToggle(id);
          } else {
            selectionHook.handleServiceToggle(id);
          }
        }}

        onSelectAll={(selectionHook as any).handleSelectAllSelected}
        onEdit={(selectionHook as any).handleEditOperation}
        onTestDurationsClick={(selectionHook as any).handleTestDurationsClick} // Need to export these
        onPatientDebtClick={(selectionHook as any).handlePatientDebtClick} // Need to export these
        onAddToPatientClick={validationHook.handleAddToPatient}
        onVisibilityClick={() => console.log("Görünürlük tıklandı")}
        columns={[
          {
            field: "name",
            headerName: t("patient-request-service.grid.columns.service-name"),
            flex: 1,
            minWidth: 200,
          },
          {
            field: "note",
            headerName: t("patient-request-service.grid.columns.note"),
            width: 150,
            editable: true,
          },
          {
            field: "duration",
            headerName: t("patient-request-service.grid.columns.duration"),
            width: 100,
            editable: true,
          },
          {
            field: "total",
            headerName: t("patient-request-service.grid.columns.total"),
            width: 100,
            editable: true,
          },
          {
            field: "quantity",
            headerName: t("patient-request-service.grid.columns.amount"),
            width: 100,
            type: "number",
            editable: true,
          },
        ]}
      />

      {selectionHook.editModalOpen && selectionHook.selectedOperationData && (
        <EditOperationModal
          open={selectionHook.editModalOpen}
          onClose={(selectionHook as any).handleCloseEditModal}
          operationData={selectionHook.selectedOperationData}
          patientId={rowID}
          onSave={(selectionHook as any).handleUpdateOperation}
        />
      )}

      {/* Constraints / Sub Modals */}
      <ProcessDirectionModal
        open={modalFlowHook.openProcessDirectionModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          const normalized = (d || "").toLowerCase();
          let olrId: number | null = null;
          if (normalized.includes("sağ") && !normalized.includes("sol")) olrId = 1;
          else if (normalized.includes("sol") && !normalized.includes("sağ")) olrId = 2;
          else if (normalized) olrId = 3;
          modalFlowHook.upsertAddInfoForPendingService("PROCESS_DIRECTION", { OlrId: olrId });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        serviceName={modalFlowHook.activeWillOpenCardServiceName || title}
      />
      <AskDoctorModal
        open={modalFlowHook.openAskDoctorModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        additionalInfoKey={
          modalFlowHook.activeWillOpenCardIndex !== null && modalFlowHook.pendingWillOpenCards[modalFlowHook.activeWillOpenCardIndex]
            ? modalFlowHook.pendingWillOpenCards[modalFlowHook.activeWillOpenCardIndex].key
            : undefined
        }
        additionalInfoParams={
          modalFlowHook.pendingConfirmSelection
            ? {
              serviceId: Number(modalFlowHook.pendingConfirmSelection.processId),
              requestDetailId: modalFlowHook.pendingConfirmSelection.processId,
              ptId: modalFlowHook.pendingConfirmSelection.ptId,
              officeId: modalFlowHook.pendingConfirmSelection.officeId,
            }
            : undefined
        }
        onSave={(d: any) => {
          const cardKey = modalFlowHook.activeWillOpenCardIndex !== null && modalFlowHook.pendingWillOpenCards[modalFlowHook.activeWillOpenCardIndex]
            ? modalFlowHook.pendingWillOpenCards[modalFlowHook.activeWillOpenCardIndex].key
            : "ASK_DOCTOR_REQUEST";
          modalFlowHook.upsertAddInfoForPendingService(cardKey, {
            PatientTypeId: d?.arrivalType ? Number(d.arrivalType) : null,
            DeptId: d?.departmentName ? Number(d.departmentName) : null,
            DoctorId: d?.doctorName ? Number(d.doctorName) : null,
            RefDoctorId: d?.referenceDoctor ? Number(d.referenceDoctor) : null,
            AssistantDoctorId: d?.assistantDoctor ? Number(d.assistantDoctor) : null,
            RefPersonelId: d?.referringPersonnel ? Number(d.referringPersonnel) : null,
          });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        serviceName={modalFlowHook.activeWillOpenCardServiceName || title}
      />
      <PregnantControlModal
        open={modalFlowHook.openPregnantControlModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("PREGNANT_CONTROL", { PregnantOrLactation: d });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
      />
      <HIVModal
        open={modalFlowHook.openHivModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("HIV", { HivInfoId: d ? Number(d) : null });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        serviceName={modalFlowHook.activeWillOpenCardServiceName || title}
      />
      <PacemakerModal
        open={modalFlowHook.openPacemakerModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("PACEMAKER_CONTROL", { PacemakerControl: d });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
      />
      <ConsultationModal
        open={modalFlowHook.openConsultationModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("CONSULTATION", {
            CrNote: d?.notes || "",
            CtDoctorId: d?.doctor ? Number(d.doctor) : null,
            CtDepartmentId: d?.department ? Number(d.department) : null,
            CtRefDoctorId: d?.isRefDoctor && d?.refDoctor ? Number(d.refDoctor) : null,
            CtCalledTime: d?.callTime || null,
            CtPatientTypeId: d?.arrivalType ? Number(d.arrivalType) : null,
          });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
      />
      <LabRequestModal
        open={modalFlowHook.openLabRequestModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("LAB_REQUEST", { LabSampleTakenSiteId: d ? Number(d) : null });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
      />
      <PathologyModal
        open={modalFlowHook.openPathologyModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        additionalInfoParams={
          modalFlowHook.pendingConfirmSelection
            ? {
              serviceId: Number(modalFlowHook.pendingConfirmSelection.processId),
              requestDetailId: modalFlowHook.pendingConfirmSelection.processId,
              ptId: modalFlowHook.pendingConfirmSelection.ptId,
              officeId: modalFlowHook.pendingConfirmSelection.officeId,
            }
            : undefined
        }
        onSave={(reqs: any) => {
          const first = Array.isArray(reqs) && reqs.length ? reqs[0] : null;
          if (first) {
            modalFlowHook.upsertAddInfoForPendingService("PATHOLOGY", {
              GDate: first.requestTime,
              MaterialCount: first.materialCount,
              MaterialType: first.materialType,
              Frozen: first.frozen,
              Description: first.explanation,
              TakenRegion: first.bodyRegion,
              ClinicalFinding: first.clinicalFindings,
            });
          }
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
      />
      <ServiceRequestKonstrastModal
        open={modalFlowHook.openContrastRequestModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("CONTRAST", {
            ContrastNote: d?.note,
            ContrastUsed: d?.isContrast,
            CreatinineValue: d?.creatinineValue,
            CreatinineDate: d?.creatinineDate
          });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        serviceName={modalFlowHook.activeWillOpenCardServiceName}
      />
      <ServiceAddKonstrastModal
        open={modalFlowHook.openContrastModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("CONTRAST", {
            ContrastNote: d?.note,
            ContrastUsed: d?.isContrast,
            CreatinineValue: d?.creatinineValue,
            CreatinineDate: d?.creatinineDate
          });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        serviceName={modalFlowHook.activeWillOpenCardServiceName}
      />
      <InvestigationRequestNoteModal
        open={modalFlowHook.openExaminationRequestModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("EXAMINATION_REQUEST", { note: d });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
      />
      <ServiceRequestVkiModal
        open={modalFlowHook.openVkiRequestModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("HEIGHT_INFO", {
            Height: d?.height ? Number(d.height) : null,
            Weight: d?.weight ? Number(d.weight) : null,
            VKI: d?.bmi ? Number(d.bmi) : null,
            PsychologicalStatusId: d?.psychStatus ? Number(d.psychStatus) : null,
            MarriageId: d?.maritalStatus ? Number(d.maritalStatus) : null,
          });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        patientId={Number(rowID)}
      />
      <ServiceAddVkiModal
        open={modalFlowHook.openVkiAddModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("HEIGHT_INFO", {
            Height: d?.height ? Number(d.height) : null,
            Weight: d?.weight ? Number(d.weight) : null,
            VKI: d?.bmi ? Number(d.bmi) : null,
            PsychologicalStatusId: d?.psychStatus ? Number(d.psychStatus) : null,
            MarriageId: d?.maritalStatus ? Number(d.maritalStatus) : null,
          });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        patientId={Number(rowID)}
      />
      <ServiceAddEgfrModal
        open={modalFlowHook.openEgfrModal}
        onClose={modalFlowHook.handleWillOpenCardModalCancelled}
        onSave={(d: any) => {
          modalFlowHook.upsertAddInfoForPendingService("EGFR", { EgfrResult: d });
          modalFlowHook.handleWillOpenCardModalClosed();
        }}
        patientId={Number(rowID)}
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={validationHook.addingToPatient}
      >
        <CircularProgress color="inherit" />
      </Backdrop>

    </OpenComponentDialog>
  );
};

export default RequestServiceModal;
